import { useState, useEffect, useMemo } from "react";
import { motion, Reorder } from "framer-motion";
import { 
  Settings, Eye, EyeOff, GripVertical, Save, RotateCcw,
  Cloud, Thermometer, Droplets, Wind, MapPin, Calendar,
  Sun, Moon, Waves, Mountain, Leaf, Plane, Radio, Heart,
  BookOpen, Star, Shield, Fish, Crosshair, Trophy
} from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";

// Icon mapping for serialization/deserialization
const ICON_MAP = {
  Cloud, Star, Calendar, Sun, Moon, Wind, Thermometer, Droplets,
  Heart, Plane, Radio, BookOpen, Waves, Mountain, Leaf, MapPin, Shield, Fish, Crosshair, Trophy
};

const DEFAULT_WIDGETS = [
  { id: "current", name: "Current Weather", iconName: "Cloud", enabled: true, order: 0 },
  { id: "aiinsights", name: "AI Insights", iconName: "Star", enabled: true, order: 1 },
  { id: "time", name: "Your Time", iconName: "Calendar", enabled: true, order: 2 },
  { id: "sun", name: "Sun Schedule", iconName: "Sun", enabled: true, order: 3 },
  { id: "moon", name: "Moon Schedule", iconName: "Moon", enabled: true, order: 4 },
  { id: "wind", name: "Wind & Atmosphere", iconName: "Wind", enabled: true, order: 5 },
  { id: "details", name: "Weather Details", iconName: "Thermometer", enabled: true, order: 6 },
  { id: "hourly", name: "24-Hour Forecast", iconName: "Thermometer", enabled: true, order: 7 },
  { id: "extended", name: "15-Day Forecast", iconName: "Calendar", enabled: true, order: 8, premium: true },
  { id: "precip-pins", name: "Rain/Snow Tracker", iconName: "MapPin", enabled: true, order: 9, premium: true },
  { id: "calendar", name: "Weather Calendar", iconName: "Calendar", enabled: true, order: 10, premium: true },
  { id: "university", name: "Weather University", iconName: "BookOpen", enabled: true, order: 11, premium: true },
  { id: "minutely", name: "Air Quality Index", iconName: "Wind", enabled: true, order: 12 },
  { id: "health", name: "Health & Activity", iconName: "Heart", enabled: true, order: 13, premium: true },
  { id: "alerts", name: "Custom Weather Alerts", iconName: "Wind", enabled: true, order: 14, premium: true },
  { id: "whatToWear", name: "What to Wear", iconName: "Sun", enabled: true, order: 15 },
  { id: "marine", name: "Lake Forecast", iconName: "Waves", enabled: true, order: 16, premium: true },
  { id: "lake-river", name: "River Conditions", iconName: "Waves", enabled: true, order: 17, premium: true },
  { id: "fishing", name: "Fishing Conditions", iconName: "Fish", enabled: true, order: 18, premium: true },
  { id: "hunting", name: "Hunting & Scouting", iconName: "Crosshair", enabled: true, order: 19, premium: true },
  { id: "ski", name: "Ski & Snow Report", iconName: "Mountain", enabled: true, order: 20, premium: true },
  { id: "flight", name: "Air Travel Weather", iconName: "Plane", enabled: true, order: 21, premium: true },
  { id: "sports-field", name: "Sports Field Weather", iconName: "Trophy", enabled: true, order: 22, premium: true },
  { id: "event-weather", name: "Festival & Event Weather", iconName: "Calendar", enabled: true, order: 23, premium: true },
  { id: "gardening", name: "Gardening Forecast", iconName: "Leaf", enabled: true, order: 24, premium: true },
  { id: "historical", name: "Weather Almanac", iconName: "BookOpen", enabled: true, order: 25, premium: true },
  { id: "storm", name: "Storm Chasers", iconName: "Radio", enabled: true, order: 26, premium: true },
  { id: "community", name: "Weather Community", iconName: "Star", enabled: true, order: 27, premium: true },
  { id: "pws", name: "Personal Weather Stations", iconName: "Radio", enabled: true, order: 28, premium: true },
  { id: "visualizations", name: "Advanced Visualizations", iconName: "MapPin", enabled: true, order: 29, premium: true },
  { id: "offline", name: "Offline Mode", iconName: "Cloud", enabled: true, order: 30, premium: true },
  { id: "radar", name: "Radar Map", iconName: "MapPin", enabled: true, order: 31, premium: true },
  { id: "precip-history", name: "7-Day Precipitation", iconName: "Droplets", enabled: true, order: 32, premium: true },
  { id: "forecast-confidence", name: "Forecast Confidence", iconName: "Shield", enabled: true, order: 33, premium: true },
  { id: "verse", name: "Verse of the Day", iconName: "Star", enabled: true, order: 34 },
];

// Helper to merge saved config with defaults (to restore icon references and ensure all widgets exist)
const mergeWithDefaults = (savedWidgets) => {
  if (!savedWidgets || savedWidgets.length === 0) {
    return DEFAULT_WIDGETS;
  }
  
  // Create a map of saved widget states
  const savedMap = new Map(savedWidgets.map(w => [w.id, w]));
  
  // Merge: use saved enabled/order, but ensure all default widgets exist with their iconName
  const merged = DEFAULT_WIDGETS.map(defaultWidget => {
    const saved = savedMap.get(defaultWidget.id);
    if (saved) {
      return {
        ...defaultWidget,
        enabled: saved.enabled,
        order: saved.order
      };
    }
    return defaultWidget;
  });
  
  // Sort by saved order
  return merged.sort((a, b) => a.order - b.order);
};

export default function DashboardCustomizer({ 
  isOpen, 
  onClose, 
  widgets, 
  onSave, 
  isDarkMode 
}) {
  // Merge saved widgets with defaults to ensure icons and all widgets are present
  const initialWidgets = useMemo(() => mergeWithDefaults(widgets), [widgets]);
  const [localWidgets, setLocalWidgets] = useState(initialWidgets);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    // When widgets prop changes, re-merge with defaults
    setLocalWidgets(mergeWithDefaults(widgets));
  }, [widgets]);

  const toggleWidget = (id) => {
    setLocalWidgets(prev => 
      prev.map(w => w.id === id ? { ...w, enabled: !w.enabled } : w)
    );
    setHasChanges(true);
  };

  const handleReorder = (newOrder) => {
    const reordered = newOrder.map((widget, index) => ({
      ...widget,
      order: index
    }));
    setLocalWidgets(reordered);
    setHasChanges(true);
  };

  const handleSave = () => {
    // Only save serializable data (no icon functions)
    const serializableWidgets = localWidgets.map(w => ({
      id: w.id,
      enabled: w.enabled,
      order: w.order
    }));
    onSave(serializableWidgets);
    setHasChanges(false);
    onClose();
  };

  const handleReset = () => {
    setLocalWidgets(DEFAULT_WIDGETS);
    setHasChanges(true);
  };

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className={`w-full max-w-lg max-h-[80vh] overflow-hidden rounded-2xl ${
          isDarkMode ? 'bg-[#0a0a0a] border border-white/10' : 'bg-white'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`p-4 border-b ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Settings className="w-5 h-5 text-[#00f2ea]" />
              <h2 className="text-lg font-bold">Customize Dashboard</h2>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} data-testid="dashboard-customizer-close-button">
              ✕
            </Button>
          </div>
          <p className="text-sm opacity-60 mt-1">
            Drag to reorder • Toggle to show/hide widgets
          </p>
        </div>

        {/* Widget List */}
        <div className="p-4 overflow-y-auto max-h-[50vh]">
          <Reorder.Group 
            axis="y" 
            values={localWidgets} 
            onReorder={handleReorder}
            className="space-y-2"
          >
            {localWidgets.map((widget) => {
              const IconComponent = ICON_MAP[widget.iconName] || Cloud;
              return (
                <Reorder.Item
                  key={widget.id}
                  value={widget}
                  className={`flex items-center gap-3 p-3 rounded-xl cursor-grab active:cursor-grabbing ${
                    isDarkMode 
                      ? 'bg-white/5 hover:bg-white/10' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  } ${!widget.enabled ? 'opacity-50' : ''}`}
                >
                  <GripVertical className="w-4 h-4 opacity-40" />
                  <IconComponent className={`w-5 h-5 ${
                    widget.premium ? 'text-[#ff0080]' : 'text-[#00f2ea]'
                  }`} />
                  <div className="flex-1">
                    <span className="font-medium">{widget.name}</span>
                    {widget.premium && (
                      <span className="ml-2 text-xs bg-gradient-to-r from-[#ff0080] to-[#7000ff] text-white px-2 py-0.5 rounded-full">
                        Premium
                      </span>
                    )}
                  </div>
                  <Switch
                    checked={widget.enabled}
                    onCheckedChange={() => toggleWidget(widget.id)}
                    data-testid={`dashboard-widget-toggle-${widget.id}`}
                  />
                </Reorder.Item>
              );
            })}
          </Reorder.Group>
        </div>

        {/* Footer */}
        <div className={`p-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleReset}
              className="text-white/60"
              data-testid="dashboard-customizer-reset-button"
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={onClose} data-testid="dashboard-customizer-cancel-button">
                Cancel
              </Button>
              <Button 
                size="sm" 
                onClick={handleSave}
                disabled={!hasChanges}
                className="bg-[#00f2ea] text-black hover:bg-[#00d4d4]"
                data-testid="dashboard-customizer-save-button"
              >
                <Save className="w-4 h-4 mr-2" />
                Save Layout
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

export { DEFAULT_WIDGETS };
